import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import Magnetic from './Magnetic';

const base =
  'group relative inline-flex select-none items-center justify-center gap-2.5 overflow-hidden whitespace-nowrap rounded-full font-medium tracking-[-0.01em] transition-[color,background-color,border-color,box-shadow] duration-500 ease-out-expo';

const variants = {
  // Solid ink pill; hover floods it with brand teal from the bottom
  primary:
    'bg-foreground text-background before:absolute before:inset-0 before:origin-bottom before:scale-y-0 before:rounded-full before:bg-[hsl(var(--brand-fill))] before:transition-transform before:duration-500 before:ease-out-expo hover:text-[hsl(var(--on-brand-fill))] hover:before:scale-y-100',
  secondary:
    'border border-foreground/15 bg-transparent text-foreground hover:border-foreground/40 hover:bg-foreground/[0.04]',
  ghost: 'text-foreground hover:text-brand-ink'
};

const sizes = {
  sm: 'h-9 px-4 text-[0.8125rem]',
  md: 'h-11 px-5 text-[0.9375rem]',
  lg: 'h-14 px-7 text-base'
};

// Label rolls up on hover: the copy slides out the top as its twin rises from below
export const RollText = ({ children }) => (
  <span className="relative inline-flex overflow-hidden">
    <span className="transition-transform duration-500 ease-out-expo group-hover:-translate-y-full motion-reduce:transition-none">
      {children}
    </span>
    <span
      aria-hidden="true"
      className="absolute inset-0 translate-y-full transition-transform duration-500 ease-out-expo group-hover:translate-y-0 motion-reduce:transition-none"
    >
      {children}
    </span>
  </span>
);

/*
 * Pill button that renders a router Link (`to`), an anchor (`href`) or a <button>.
 * `arrow` adds a ↗ that turns to → on hover; `magnetic` makes it follow the pointer.
 */
const Button = ({
  to,
  href,
  variant = 'primary',
  size = 'md',
  arrow = false,
  magnetic = false,
  className,
  children,
  ...rest
}) => {
  const classes = cn(base, variants[variant], sizes[size], className);
  const content = (
    <>
      <span className="relative z-10 inline-flex items-center gap-2.5">
        <RollText>{children}</RollText>
        {arrow && (
          <ArrowUpRight
            size={size === 'lg' ? 18 : 16}
            strokeWidth={1.8}
            aria-hidden="true"
            className="transition-transform duration-500 ease-out-expo group-hover:rotate-45"
          />
        )}
      </span>
    </>
  );

  let element;
  if (to) element = <Link to={to} className={classes} {...rest}>{content}</Link>;
  else if (href) element = <a href={href} className={classes} {...rest}>{content}</a>;
  else element = <button type="button" className={classes} {...rest}>{content}</button>;

  return magnetic ? <Magnetic>{element}</Magnetic> : element;
};

export default Button;
